import React, { useMemo, useState } from "react";
import { Button, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";
import { Link } from "react-router-dom";
import { SingleCounsellingData } from "../../types/WellnessCounsellingTypes";
import { sessionNotesBadge } from "./recentSessionsNotes";
import { AppTable } from "../../components/common";

const { Text } = Typography;

function getUserId(record: SingleCounsellingData): string {
  const user: any = record?.user;
  if (typeof user === "string") return user;
  if (user?._id) return String(user._id);
  return "";
}

function resolveCompanySegment(record: SingleCounsellingData): string {
  const user: any = record?.user ?? {};
  const companyName =
    (record as any)?.companyName ||
    user?.companyName ||
    user?.company ||
    user?.company_name ||
    (record as any)?.company ||
    "-";

  return encodeURIComponent(String(companyName));
}

type Props = {
  rows: SingleCounsellingData[];
  loading: boolean;
  search: string;
};

export default function RecentSessionsTable({
  rows,
  loading,
  search,
}: Props) {

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const counselorRaw = r.previousCounselors as unknown;
      const counselor =
        Array.isArray(counselorRaw) && counselorRaw.length > 0
          ? String(counselorRaw[0]).toLowerCase()
          : String(counselorRaw ?? "").toLowerCase();
      return (
        String(r._id ?? "").toLowerCase().includes(q) ||
        getUserId(r).toLowerCase().includes(q) ||
        String(r.timeSlot ?? "").toLowerCase().includes(q) ||
        String(r.sessionStatus ?? "").toLowerCase().includes(q) ||
        counselor.includes(q) ||
        moment(r.date).format("DD/MM/YY D MMM ddd").toLowerCase().includes(q)
      );
    });
  }, [rows, search]);

  const columns: ColumnsType<SingleCounsellingData> = [
    {
      title: "Booking ID",
      dataIndex: "_id",
      key: "_id",
      width: 150,
      render: (id: string) => <span className="recentSessions__bookingId">{id} </span>,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (date: string) => (
        <span className="recentSessions__primaryCell">
          {date ? moment(date).format("DD/MM/YY") : "-"}
        </span>
      ),
    },
    {
      title: "Time",
      dataIndex: "timeSlot",
      key: "timeSlot",
      width: 150,
      render: (time: string) => (
        <span className="recentSessions__primaryCell">{time || "-"}</span>
      ),
    },
    {
      title: "Session Notes",
      key: "sessionNotes",
      width: 140,
      render: (_, record) => {
        const { tone, label } = sessionNotesBadge(record);
        return (
          <Tag
            className={`recentSessions__notesTag recentSessions__notesTag--${tone}`}
          >
            {tone === "pending" ? "Pending" : tone === "complete" ? "Completed" : label}
          </Tag>
        );
      },
    },
    {
      title: "Session Reports",
      dataIndex: "hasSessionReports",
      key: "hasSessionReports",
      width: 150,
      render: (value: boolean | undefined) => (
        <Tag
          className={`recentSessions__notesTag recentSessions__notesTag--${
            value ? "complete" : "pending"
          }`}
        >
          {value ? "Completed" : "Pending"}
        </Tag>
      ),
    },
    {
      title: "Session Resources",
      dataIndex: "hasSessionResources",
      key: "hasSessionResources",
      width: 160,
      render: (value: boolean | undefined) => (
        <Tag
          className={`recentSessions__notesTag recentSessions__notesTag--${
            value ? "complete" : "pending"
          }`}
        >
          {value ? "Completed" : "Pending"}
        </Tag>
      ),
    },
    {
      title: "Client ID",
      dataIndex: "user",
      key: "user",
      width: 140,
      render: (_, record) => getUserId(record) || "-",
    },
    {
      title: "Action",
      key: "qaView",
      width: 130,
      render: (_, record) => {
        const userId = getUserId(record);
        if (!userId) return <Text type="secondary">-</Text>;

        return (
          <Link
            to={`/user-files-session-history/${resolveCompanySegment(
              record
            )}/${encodeURIComponent(String(userId))}`}
          >
            <Button type="default" className="recentSessions__pdfBtn">
              View
            </Button>
          </Link>
        );
      },
    },
  ];

  return (
    <>
      <AppTable
        className="recentSessions__table"
        loading={loading}
        rowKey={(record) => record._id}
        dataSource={filteredRows}
        columns={columns}
      />
    </>
  );
}
